/**
 * NightlifeDrawerBlock — drawer-scaled extension of NightlifeCardBlock.
 *
 * Founder spec 8 Feb 2026 (Hï Ibiza · DJ Mag / W50B card frame):
 *   for nightclub + bar venues the drawer must surface
 *     (1) the SOURCED authority badge (slate · attributed)
 *     (2) the sourced known-for one-liner (italic)
 *     (3) the high-decay row ALWAYS prefixed with
 *         "Desk confirms before you go" — never a hard claim.
 *
 *   Concierge® · by hand stays the recommended HERO for the route
 *   ladder (door access is relationship work) — that's wired in
 *   route-utils.js NIGHTLIFE_LADDER and rendered by the drawer's
 *   normal route block.  This component renders the SOURCED + HEDGED
 *   factual fields that surround that hero.
 *
 * The component is intentionally a sibling of NightlifeCardBlock —
 *   - card version (card typography · compact dl grid)
 *   - drawer version (drawer typography · larger labels, more breathing room)
 *
 * Asymmetric Trust Rule:
 *   - renders only when at least one nightlife signal is present
 *   - never paints a labelled-but-empty row.
 */
import React from "react";


export function NightlifeDrawerBlock({ venue }) {
    const at = venue?.archive_texture || {};
    const cat = at.category_render_type || venue?.category_render_type;
    if (cat !== "nightlife" && cat !== "bar") return null;

    const authBadge = at.authority_badge_sourced;
    const authSource = at.authority_badge_source;
    const rankDjmag = at.rank_djmag_top100_2026;
    const rankW50b = at.rank_world50best_2025;
    const knownForSourced = at.known_for_sourced;

    const highDecay = [
        { key: "tonight_lineup", label: "Tonight" },
        { key: "table_minimum",  label: "Table minimum" },
        { key: "dress_code",     label: "Dress" },
        { key: "door_time",      label: "Doors" },
        { key: "resident_dj",    label: "Resident" },
        { key: "best_night",     label: "Best night" },
        { key: "entry_model",    label: "Entry" },
        { key: "signature_drink", label: "Signature drink" },
    ].filter(f => {
        const v = at[f.key];
        return typeof v === "string" && v.trim().length > 0;
    });

    // Render only when at least ONE signal is present
    if (!authBadge && rankDjmag == null && rankW50b == null
        && !knownForSourced && highDecay.length === 0) {
        return null;
    }

    return (
        <div
            data-testid="drawer-nightlife-block"
            data-category={cat}
            style={{
                marginTop: 14,
                paddingTop: 12,
                borderTop: "1px solid var(--t-line2)",
                display: "flex",
                flexDirection: "column",
                gap: 10,
            }}
        >
            {/* Authority badge · sourced slate chip */}
            {authBadge ? (
                <div
                    data-testid="drawer-nightlife-authority-badge"
                    title={
                        authSource
                            ? `Sourced from ${authSource}`
                            : `Sourced from ${authBadge}`
                    }
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        alignSelf: "flex-start",
                        background: "var(--t-slate-wash)",
                        color: "var(--t-ink2)",
                        border: "1px solid var(--t-line2)",
                        borderRadius: 999,
                        padding: "5px 12px",
                        fontSize: 12,
                        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                        fontWeight: 500,
                        letterSpacing: "0.01em",
                    }}
                >
                    {(rankDjmag != null || rankW50b != null) ? (
                        <span style={{ fontWeight: 700, color: "var(--t-ink)" }}>
                            #{rankDjmag ?? rankW50b} ·{" "}
                        </span>
                    ) : null}
                    {authBadge}
                </div>
            ) : null}

            {/* Sourced known-for one-liner */}
            {knownForSourced ? (
                <p
                    data-testid="drawer-nightlife-known-for-sourced"
                    style={{
                        margin: 0,
                        fontSize: 13,
                        color: "var(--t-ink2)",
                        fontStyle: "italic",
                        lineHeight: 1.5,
                    }}
                >
                    {knownForSourced}
                </p>
            ) : null}

            {/* High-decay row · always hedged */}
            {highDecay.length > 0 ? (
                <div
                    data-testid="drawer-nightlife-high-decay-row"
                    style={{
                        marginTop: 4,
                        padding: "12px 14px",
                        background: "var(--t-pending-wash)",
                        border: "1px solid var(--t-line2)",
                        borderRadius: 12,
                        color: "var(--t-ink2)",
                    }}
                >
                    <div
                        className="t-mono uppercase"
                        data-testid="drawer-nightlife-high-decay-hedge"
                        style={{
                            fontSize: 10,
                            letterSpacing: "0.16em",
                            color: "var(--t-pending)",
                            fontWeight: 700,
                            marginBottom: 8,
                        }}
                    >
                        Desk confirms before you go
                    </div>
                    <dl
                        style={{
                            margin: 0,
                            display: "grid",
                            gridTemplateColumns: "max-content 1fr",
                            columnGap: 14,
                            rowGap: 4,
                            fontSize: 13,
                        }}
                    >
                        {highDecay.map((f) => (
                            <React.Fragment key={f.key}>
                                <dt
                                    style={{
                                        color: "var(--t-ink3)",
                                        fontWeight: 500,
                                    }}
                                    data-testid={`drawer-nightlife-field-${f.key}-label`}
                                >
                                    {f.label}
                                </dt>
                                <dd
                                    style={{
                                        margin: 0,
                                        color: "var(--t-ink)",
                                        lineHeight: 1.45,
                                    }}
                                    data-testid={`drawer-nightlife-field-${f.key}`}
                                >
                                    {at[f.key]}
                                </dd>
                            </React.Fragment>
                        ))}
                    </dl>
                </div>
            ) : null}
        </div>
    );
}
